import type { InboxForemanFacet, InboxPcoFacet } from '@coffman-tm/shared';
import { INBOX_UNASSIGNED_PCO, padPcoNumber } from '@coffman-tm/shared';

/**
 * One <option> for the master-list filter bar. `value` is what rides the
 * request verbatim — `''` means "All" (the field is omitted from the query),
 * `'unassigned'` is the PCO-dimension sentinel, anything else is an exact
 * `pco_id` or `foreman_name`.
 */
export interface InboxFilterOption {
  value: string;
  label: string;
}

/**
 * Ellipsis-truncate for the ~280px list column. A native <select>'s CLOSED
 * state respects CSS text-overflow, but the open option list is OS-rendered
 * and mostly ignores it (Chrome in particular), so long text is cut here
 * rather than relying on CSS to do it in the dropdown itself.
 */
export function truncateLabel(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/**
 * PCO-dimension filter options: "All PCOs (n)" first, "Unassigned (n)" when
 * any exist, then one option per faceted PCO in the order the API already sorted
 * them (biggest pile first). Both `facets` and `unassignedCount` empty means
 * there is no PCO breakdown to filter by yet — returns `[]` so the caller
 * renders nothing rather than a single-option, do-nothing control.
 */
export function buildPcoFilterOptions(
  facets: InboxPcoFacet[],
  unassignedCount: number,
): InboxFilterOption[] {
  if (facets.length === 0 && unassignedCount === 0) return [];
  const total = facets.reduce((n, f) => n + f.pending_block_count, 0) + unassignedCount;
  // The dimension word lives in the option, not an eyebrow beside the select —
  // the filter bar renders bare controls (see InboxFilterBar).
  const options: InboxFilterOption[] = [{ value: '', label: `All PCOs (${total})` }];
  if (unassignedCount > 0) {
    // The shared constant, not a literal — three copies of one string is how
    // a renamed sentinel silently turns this filter into "everything".
    options.push({ value: INBOX_UNASSIGNED_PCO, label: `Unassigned (${unassignedCount})` });
  }
  for (const f of facets) {
    options.push({
      value: f.pco_id,
      label: `${padPcoNumber(f.pco_number)} — ${truncateLabel(f.description, 26)} (${f.pending_block_count})`,
    });
  }
  return options;
}

/**
 * Foreman-dimension filter options: "All foremen (n)" first, then one option per
 * foreman with pending work, in the order the API sorted them. Empty facets
 * means there is nothing to filter by yet — returns `[]`.
 */
export function buildForemanFilterOptions(facets: InboxForemanFacet[]): InboxFilterOption[] {
  if (facets.length === 0) return [];
  const total = facets.reduce((n, f) => n + f.pending_block_count, 0);
  const options: InboxFilterOption[] = [{ value: '', label: `All foremen (${total})` }];
  for (const f of facets) {
    options.push({
      value: f.foreman_name,
      label: `${truncateLabel(f.foreman_name, 22)} (${f.pending_block_count})`,
    });
  }
  return options;
}
